import {
  BlogArticleTranslationEntity,
  getDirectusInstance,
  resolveTranslation,
} from "@fhlib/directus";
import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { BlogArticleTemplate } from "@templates/blog";
import { routes } from "@fhlib/routes";
import { useTranslation } from "next-i18next";
import Head from "next/head";

export const getServerSideProps: GetServerSideProps<BlogArticleProps> = async ({
  locale,
  defaultLocale,
  query,
  params,
}) => {
  const directus = await getDirectusInstance();

  if (!params) {
    throw new Error("Params invalid");
  }

  const { slug } = params;

  if (slug === undefined) {
    throw new Error("Invalid slug");
  }

  const blogArticleTranslations = await directus
    .items("blog_articles_translations")
    .readByQuery({
      fields: ["blog_articles_id"],
      limit: 1,
      filter: {
        slug: slug as string,
      },
    });

  let blog_article_id: number | undefined;

  if (blogArticleTranslations.data && blogArticleTranslations.data.length > 0) {
    blog_article_id = blogArticleTranslations.data[0]
      .blog_articles_id as number;
  }

  const redirectToBlog = {
    redirect: {
      destination: routes["blog"][locale ?? defaultLocale ?? "en"],
      permanent: false,
    },
    props: {},
  };

  if (blog_article_id === undefined) {
    return redirectToBlog;
  }

  const articlesResponse = await directus
    .items("blog_articles")
    .readOne(blog_article_id, {
      fields: ["cover", "translations.*"] as any,
      filter: { status: { _eq: "published" } },
    });

  if (!articlesResponse) {
    return redirectToBlog;
  }

  const defaultTranslated = resolveTranslation(
    "en",
    "en",
    articlesResponse.translations as BlogArticleTranslationEntity[]
  );

  const translated = resolveTranslation(
    locale,
    defaultLocale,
    articlesResponse.translations as BlogArticleTranslationEntity[]
  );

  const title =
    !translated.title || translated.title.length === 0
      ? defaultTranslated.title
      : translated.title;

  let content =
    !translated.content || translated.content.length === 0
      ? defaultTranslated.content
      : translated.content;

  return {
    props: {
      slug,
      title,
      content,
      cover: articlesResponse.cover,
      ...(await serverSideTranslations(locale ?? "en", [
        "common",
        "homes-sheet",
        "property",
        "forms",
      ])),
    } as BlogArticleProps,
  };
};

type BlogArticleProps = {
  title: string;
  content: string;
  slug: string;
  cover: string;
};

export default function BlogArticlePage({
  title,
  content,
  slug,
  cover,
}: BlogArticleProps) {
  const { t } = useTranslation("blog");

  return (
    <>
      <Head>
        <title>
          {title} - {t("title")} - FeelingHome
        </title>
      </Head>
      <BlogArticleTemplate
        title={title}
        content={content}
        slug={slug}
        cover={cover}
      />
    </>
  );
}
